import isEqual from 'lodash/isEqual';
import nullthrows from 'nullthrows';
import React, { ComponentType, useCallback, useEffect, useRef } from 'react';

import AuthManager from '@/features/auth/hooks/use-auth/authManager';
import { Viewer, $Subtract } from '@/shared/types/tile-packet.types';
import Analytics from '@/shared/utils/analytics/analytics';
import { useDispatch, useSelector } from '@/store/hooks';

export type AuthProps = {
  legacyLogout?: () => Promise<void>;
  getSessionSecret: () => string;
  setMetadata: (metadata: { appetizeCode: string }) => Promise<void>;
  viewer?: Viewer | undefined;
  dispatch: (action: { type: 'UPDATE_VIEWER'; viewer: Viewer | null | undefined }) => void;
};

const Auth = new AuthManager();

export default function withAuth<P extends AuthProps>(
  Comp: ComponentType<P>,
): ComponentType<$Subtract<P, AuthProps>> {
  function WithAuthWrapper(props: any) {
    const viewer = useSelector((state: { viewer: Viewer | null }) => state.viewer);
    const dispatch = useDispatch();

    // The latest viewer is read by handleSetMetadata's continuation —
    // capture in a ref so the memoized callbacks don't have to depend
    // on it.
    const viewerRef = useRef(viewer);
    viewerRef.current = viewer;

    const updateViewer = useCallback(async () => {
      const { accessToken, sessionSecret } = Auth;
      if (accessToken || sessionSecret) {
        const fresh = await Auth.getProfile();
        const prev = viewerRef.current;
        dispatch({ type: 'UPDATE_VIEWER', viewer: fresh } as any);
        if (fresh && !isEqual(prev, fresh)) {
          Analytics.getInstance().identify({ username: fresh.username }, fresh.id);
        }
      } else {
        dispatch({ type: 'UPDATE_VIEWER', viewer: null } as any);
      }
    }, [dispatch]);

    // One-shot mount probe — replaces the legacy componentDidMount.
    useEffect(() => {
      updateViewer();
    }, [updateViewer]);

    const handleGetSessionSecret = useCallback(() => Auth.sessionSecret, []);

    // TODO(tc): replace this once we talk to graphql elsewhere
    const handleSetMetadata = useCallback(
      async (newMetadata: { appetizeCode: string }) => {
        const { accessToken, sessionSecret } = Auth;
        if (!accessToken && !sessionSecret) return;
        const endpoint = `${nullthrows(process.env.API_SERVER_URL)}/--/graphql`;
        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              ...(sessionSecret ? { 'Expo-Session': sessionSecret } : {}),
            },
            body: JSON.stringify({
              query: `mutation ($newMetadata: UserDataInput!) {
                        me {
                          updateProfile(userData: $newMetadata) {
                            id
                          }
                        }
                      }`,
              variables: { newMetadata },
            }),
          });
          const json = await response.json();
          if (!json.errors) updateViewer();
        } catch {
          /* nop */
        }
      },
      [updateViewer],
    );

    const handleLegacyLogout = useCallback(async () => {
      await Auth.legacyLogout();
      updateViewer();
    }, [updateViewer]);

    return (
      <Comp
        // @ts-ignore — passed-through props plus injected auth surface
        legacyLogout={Auth.isLegacyLogoutEnabled ? handleLegacyLogout : undefined}
        getSessionSecret={handleGetSessionSecret}
        setMetadata={handleSetMetadata}
        viewer={viewer}
        dispatch={dispatch as any}
        {...props}
      />
    );
  }

  WithAuthWrapper.displayName = `withAuth(${Comp.displayName || Comp.name})`;
  return WithAuthWrapper as any;
}
